/* Add to cart without losing the page, and confirm the spelling.
 *
 * Intercepts the product form. A full page POST answers "did it work" by navigating
 * away from the thing the customer just configured; the one question at that moment is
 * whether the name went in right, and that is cheapest to fix while it is still on
 * screen.
 *
 * Personalised purchases require a current preview specification. A failed
 * request never triggers a second add automatically.
 *
 * After adding, the one cart opens (the cart sheet, assets/il-cart-sheet.js) with the
 * cart section that the add itself returned (sections=cart), so the customer sees the
 * cart a moment after pressing the button, with no second request (owner, 1 October
 * 2026: one cart, the drawer on every page).
 */
(function () {
  const _i18n=(()=>{try{const s=document.getElementById('il-i18n');return s?JSON.parse(s.textContent):{};}catch{return {};}})();
  const t=(key,p={})=>{const v=_i18n[key]??key;return Object.entries(p).reduce((s,[k,val])=>s.replace('{'+k+'}',()=>val),String(v));};
  const form = document.getElementById('il-buy');
  if (!form) return;

  let submitting = false;
  const message = document.createElement('p');
  message.setAttribute('role', 'alert');
  message.setAttribute('data-cart-error', '');
  message.hidden = true;
  form.appendChild(message);

  const variants = JSON.parse(document.querySelector('[data-variant-data]')?.textContent || '[]');
  function unavailable() {
    const selected = form.querySelector('[data-variant-id]')?.value;
    return variants.length > 0 && !variants.find(v => String(v.id) === selected)?.available;
  }

  /* What keeps Add to cart off: a sold-out choice, or two or three names not all typed
     (assets/il-names.js). Never the preview (owner, 22 October 2026, after a customer whose
     preview would not load could not order: "I want the customer to be able to order it even if
     it does not work"). A line added without a finished preview carries the typed name and
     _Preview, and the order run makes it from the name (tools/production-study/process-orders.mjs);
     a name the preview flags for a check carries that flag. */
  function previewIssue() {
    if (unavailable()) return t('cart.unavailable');
    return window.ilNames?.issue() || '';
  }

  /* A preview still drawing when Add is pressed is waited for, a few seconds, so the line keeps
     its design and picture; a failed one is not. */
  const SPEC_FIELDS = ['_Sign preview spec', '_Keychain preview spec', '_Magnet preview spec'];
  const NOT_SHOWN = 'Not shown on the customer\'s device: make it from the typed name';
  function previewSettled(limit) {
    const preview = document.querySelector('[data-union-preview]');
    const done = () => {
      const state = preview?.dataset.state;
      if (!preview || !['loading', 'updating', 'ready'].includes(state)) return true;
      if (state !== 'ready') return false;
      const spec = preview.querySelector('[data-sign-spec],[data-keychain-spec],[data-magnet-spec]');
      try { return !spec || spec.disabled || JSON.parse(spec.value).name === nameInput?.value.trim(); } catch { return true; }
    };
    return new Promise(resolve => {
      const end = Date.now() + limit;
      const tick = () => (done() || Date.now() > end ? resolve() : setTimeout(tick, 150));
      tick();
    });
  }

  function syncButtons() {
    const disabled = submitting || !!previewIssue();
    document.querySelectorAll('.add, .add2, .buybar button, .sbright button').forEach(button => { button.disabled = disabled; });
  }
  const previewRoot = document.querySelector('[data-union-preview]');
  if (previewRoot) new MutationObserver(syncButtons).observe(previewRoot, {subtree:true, attributes:true, attributeFilter:['data-state','disabled']});
  form.addEventListener('click', () => queueMicrotask(syncButtons));
  document.querySelector('[data-text-input]')?.addEventListener('input', syncButtons);
  window.ilCartSync = syncButtons;
  syncButtons();

  /* A cart line's picture is the customer's own design: the product page draws it at the
     moment of adding (il-union-preview.js, cardSnapshot) and it is kept in this browser
     for that line, for the cart (sections/cart.liquid and assets/il-cart-sheet.js read the
     same key). Storage can be unavailable or full; the line then keeps the product image. */
  const THUMB = 'il-cart-thumb:';
  const thumbs = new Map();
  function thumb(key) {
    if (thumbs.has(key)) return thumbs.get(key);
    try { return localStorage.getItem(THUMB + key); } catch { return null; }
  }
  function keepThumb(key, url) {
    thumbs.set(key, url);
    try { localStorage.setItem(THUMB + key, url); } catch { /* shown from memory for this visit */ }
  }
  function pruneThumbs(cart) {
    try {
      const current = new Set(cart.items.map(item => THUMB + item.key));
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const name = localStorage.key(i);
        if (name && name.startsWith(THUMB) && !current.has(name)) localStorage.removeItem(name);
      }
    } catch { /* nothing to tidy */ }
  }

  /* The set line on a keychain's page (sections/product.liquid) opens the design's sign with
     the name typed here, as the cart's offer and the cross-sell carry it (preview_name). */
  const nameInput = document.querySelector('[data-text-input][form="il-buy"]');
  const setLink = document.querySelector('[data-set-sign-link]');
  if (setLink && nameInput) {
    const base = setLink.href;
    const carry = () => {
      const url = new URL(base, location.origin);
      const name = nameInput.value.trim();
      if (/^[A-Za-z]{1,12}$/.test(name)) url.searchParams.set('preview_name', name);
      const theme = new URLSearchParams(location.search).get('preview_theme_id');
      if (theme) url.searchParams.set('preview_theme_id', theme);
      setLink.href = url.href;
    };
    nameInput.addEventListener('input', carry);
    carry();
  }

  /* "Buying for two?" (keychains and magnets, sections/product.liquid): the same add, then
     the name field empties for the second name and the cart stays closed; the header's count
     follows, as the cart sheet keeps it. */
  const secondStatus = document.querySelector('[data-second-status]');
  function secondNameNext(cart, added) {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = el.textContent.replace(/\(\d+\)/, '(' + cart.item_count + ')');
    });
    if (secondStatus) {
      // Said under the name field, where the page goes next (on a phone the button is far below).
      nameInput?.closest('.inp')?.after(secondStatus);
      secondStatus.hidden = false;
      secondStatus.textContent = t('cart.second_added', {name: added});
    }
    if (nameInput) {
      nameInput.value = '';
      nameInput.dispatchEvent(new Event('input', {bubbles: true}));
      nameInput.focus({preventScroll: true});
      nameInput.scrollIntoView({block: 'center', behavior: 'smooth'});
    }
  }

  form.addEventListener('submit', async (e) => {
    if (submitting) { e.preventDefault(); return; }
    const second = !!e.submitter?.hasAttribute('data-second-name');
    const issue = previewIssue();
    if (issue) {
      e.preventDefault(); message.hidden = false; message.textContent = issue;
      message.scrollIntoView({block: 'nearest'}); return;
    }
    if (!form.reportValidity()) {
      e.preventDefault();
      return;
    }
    if (!window.fetch) return;
    e.preventDefault();
    submitting = true;
    syncButtons();
    message.hidden = true;

    const btn = second ? e.submitter : form.querySelector('.add');
    const original = btn ? btn.textContent : '';
    const added = nameInput ? nameInput.value.trim() : '';
    if (secondStatus) secondStatus.hidden = true;
    if (btn) {
      btn.disabled = true;
      btn.textContent = btn.dataset.adding || t('cart.adding');
    }

    /* Started by the preview at this submit, before anything about the design can change. */
    const snapshot = form.ilCartSnapshot;
    form.ilCartSnapshot = null;
    /* Two or three names, or a sign with its keychain (assets/il-names.js): one line per item,
       each with its own checked design and picture, added together in one request. */
    const many = !second && !!window.ilNames?.active();

    try {
      let item;
      if (many) {
        const lines = await window.ilNames.lines();
        const variant = form.querySelector('[data-variant-id]')?.value;
        const res = await fetch((window.Shopify?.routes?.root || '/') + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: lines.map(line => ({ id: Number(line.id || variant), quantity: 1, properties: line.properties })), sections: 'cart', sections_url: location.pathname }),
        });
        if (!res.ok) {
          const problem = await res.json().catch(() => ({}));
          const error=new Error(problem.description || t('cart.add_failed'));
          error.rejected=true;throw error;
        }
        const answer = await res.json();
        // Each line's picture goes to the line with its own properties (the same name twice is one line).
        const same = (sent, got) => Object.keys(sent || {}).every(key => String(sent[key]) === String(got?.[key] ?? ''));
        const used = new Set();
        (answer.items || []).forEach((line, i) => {
          let from = lines.findIndex((sent, j) => !used.has(j) && same(sent.properties, line.properties));
          if (from < 0) from = i;
          used.add(from);
          if (lines[from]?.picture && line.key) keepThumb(line.key, lines[from].picture);
        });
        item = {...(answer.items || [])[0], sections: answer.sections};
        window.ilNames.added();
      } else {
        await previewSettled(8000);
        // The design as drawn now (the preview may have finished while waiting): its colours
        // saved into it, and its picture.
        const preview = document.querySelector('[data-union-preview]');
        const drawn = preview?.ilCardSnapshot ? preview.ilCardSnapshot() : snapshot;
        // The cart section comes back with the add, drawn after it (Section Rendering API).
        const data = new FormData(form);
        if (preview && !SPEC_FIELDS.some(field => data.get(`properties[${field}]`))) data.append('properties[_Preview]', NOT_SHOWN);
        data.append('sections', 'cart');
        data.append('sections_url', location.pathname);
        const res = await fetch((window.Shopify?.routes?.root || '/') + 'cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: data,
        });

        if (!res.ok) {
          const problem = await res.json().catch(() => ({}));
          const error=new Error(problem.description || t('cart.add_failed'));
          error.rejected=true;throw error;
        }
        item = await res.json();
        const picture = await Promise.race([Promise.resolve(drawn).catch(() => null), new Promise(resolve => setTimeout(resolve, 1500, null))]);
        if (picture && item.key) keepThumb(item.key, picture);
      }

      const response=await fetch((window.Shopify?.routes?.root || '/') + 'cart.js');
      if(!response.ok)throw Error('Cart could not be refreshed');
      const cart=await response.json();
      pruneThumbs(cart);

      if (second) secondNameNext(cart, added);
      else if (window.ilCartSheet) window.ilCartSheet.open({ html: item.sections && item.sections.cart, cart });
      else location.href = (window.Shopify?.routes?.root || '/') + 'cart';
      document.dispatchEvent(new CustomEvent('il:cart-updated', {detail:{cart, item}}));
    } catch (err) {
      // A failed response may follow a successful add. Never retry a mutation
      // automatically, including when the subsequent cart-summary fetch fails.
      message.hidden = false;
      if(err.rejected){message.textContent=err.message;return;}
      message.textContent = t('cart.confirm_update_failed')+' ';
      const link = document.createElement('a');
      link.href = (window.Shopify?.routes?.root || '/') + 'cart';
      link.textContent = t('cart.view_cart'); message.appendChild(link);
      return;
    } finally {
      submitting = false;
      if (btn) {
        btn.textContent = original;
      }
      syncButtons();
    }
  });
})();
